/* eslint-disable react/no-unescaped-entities */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Phone, ArrowRight } from "lucide-react";
import axios from "axios";
import SEO from "@/components/site/SEO";
import { gbp } from "@/lib/wallet";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Rates() {
  const [rates, setRates] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/rates`);
        setRates(data.items || []);
      } finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return rates;
    const needle = q.trim().toLowerCase();
    return rates.filter((r) =>
      r.country_name.toLowerCase().includes(needle) ||
      r.destination.toLowerCase().includes(needle) ||
      r.prefix.startsWith(needle.replace(/\D/g, "")),
    );
  }, [rates, q]);

  return (
    <div className="bg-brand-bgSoft text-brand-ink" data-testid="rates-page">
      <SEO title="Calling Rates" path="/rates"
        description="Transparent per-minute rates to 50+ destinations. Per-second billing. No hidden charges." />

      <section className="bg-brand-ink text-white">
        <div className="container-page py-16 lg:py-24">
          <p className="label-eyebrow text-brand-green">Rates</p>
          <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl lg:text-6xl">
            Transparent, per-second pricing
          </h1>
          <p className="mt-4 text-white/70 max-w-2xl">
            Save up to 80% versus traditional carriers. No connection fees, no hidden charges — pay only for what you talk.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/register" className="inline-flex items-center gap-2 rounded-full bg-brand-green hover:bg-brand-greenHover px-5 py-3 text-sm font-semibold shadow-sm" data-testid="rates-cta-signup">
              Open a free account <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/how-it-works" className="text-sm font-semibold text-white/80 hover:text-white">How it works →</Link>
          </div>
        </div>
      </section>

      <section className="container-page py-12">
        <div className="flex items-center gap-3 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted" />
            <input
              type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search country or prefix (e.g. Nigeria, 234)"
              data-testid="rates-search"
              className="w-full rounded-full border border-brand-line bg-white pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-brand-green"
            />
          </div>
          <p className="text-xs text-brand-muted">{loading ? "Loading…" : `${filtered.length} of ${rates.length}`}</p>
        </div>

        <div className="rounded-2xl border border-brand-line bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-brand-bgSoft text-left text-xs uppercase tracking-wider text-brand-muted">
                <tr>
                  <th className="py-3 px-4">Country</th>
                  <th className="py-3 px-4">Destination</th>
                  <th className="py-3 px-4">Prefix</th>
                  <th className="py-3 px-4 text-right">Rate / min</th>
                  <th className="py-3 px-4 text-right">Billing</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-line">
                {loading && (
                  <tr><td colSpan="5" className="py-12 text-center text-brand-muted">Loading rates…</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan="5" className="py-12 text-center text-brand-muted">No matches.</td></tr>
                )}
                {filtered.map((r) => (
                  <tr key={r.id} data-testid={`rate-row-${r.id}`} className="hover:bg-brand-bgSoft">
                    <td className="py-3 px-4 font-semibold">{r.country_name}</td>
                    <td className="py-3 px-4 text-brand-ink/80">{r.destination}</td>
                    <td className="py-3 px-4 font-mono text-xs">+{r.prefix}</td>
                    <td className="py-3 px-4 text-right font-bold text-brand-greenDark">{gbp(r.rate_per_minute)}</td>
                    <td className="py-3 px-4 text-right text-xs text-brand-muted">
                      {r.billing_increment_seconds === 1 ? "Per-second" : `${r.billing_increment_seconds}s`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <p className="mt-4 text-xs text-brand-muted">
          Rates are inclusive of UK VAT where applicable. Operator surcharges may apply on some mobile networks. Special service numbers and premium-rate numbers excluded.
        </p>
      </section>

      <section className="bg-brand-ink text-white">
        <div className="container-page py-14 grid md:grid-cols-3 gap-8 items-start">
          <div>
            <Phone className="h-7 w-7 text-brand-green" />
            <h2 className="mt-3 font-display font-black text-2xl">Ready to call home?</h2>
            <p className="mt-2 text-white/70 text-sm">Top up your wallet and start saving today.</p>
          </div>
          <div className="md:col-span-2 flex md:justify-end items-center gap-3">
            <Link to="/register" className="inline-flex items-center gap-2 rounded-full bg-brand-green hover:bg-brand-greenHover px-5 py-3 text-sm font-semibold" data-testid="rates-cta-signup-bottom">
              Sign up free <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact-us" className="text-sm font-semibold text-white/80 hover:text-white">Talk to us →</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
